import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, X as XIcon, Trash2, Plus, ShieldCheck, Ban } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { confirmDialog } from "../lib/confirm";
import { useAuth } from "../lib/auth";
import { DatePicker } from "../components/DatePicker";
import { AdminPinPromptDialog } from "../components/admin/AdminPinPromptDialog";
import { BRAND_COLORS, STATUS_COLORS, withAlpha, projectColorKey } from "../lib/bookingStatus";
import { StatusBadge } from "../components/StatusBadge";
import { SeenDot, SeenFilterToggle, isUnseenAssignment } from "../components/SeenIndicator";
import { deDate } from "../lib/dateFormat";
import { deCurrency } from "../lib/numberFormat";

const STATUS_LABEL = { planning: "Planung", in_progress: "In Arbeit", on_hold: "Pausiert", completed: "Abgeschlossen", cancelled: "Storniert" };
const MS_STATUS_LABEL = { pending: "Offen", in_progress: "In Arbeit", done: "Erledigt" };
const MS_STATUS_COLOR = {
  pending:     { bg: STATUS_COLORS.grey,  fg: BRAND_COLORS.navy },
  in_progress: { bg: STATUS_COLORS.blue,  fg: BRAND_COLORS.ivory },
  done:        { bg: STATUS_COLORS.green, fg: BRAND_COLORS.ivory },
};

// Feb 2026 · 9 Sep 2026 · User-Directive „Vollständige Badge- und
// Farblogik" (finale Fassung):
//   • GRAU  = NICHT zugeteilt ODER Status ∈ {planning, on_hold}
//   • BLAU  = ZUGETEILT UND Status = in_progress  (nur In Bearbeitung!)
//   • GRÜN  = completed
//   • SLATE = cancelled (rutscht ganz nach unten)
//
// Der rote Badge auf der Projekte-Seite zählt EXAKT die Projekte, die
// laut dieser Regel als GRAU eingestuft sind — Planungs- und Pausiert-
// Projekte mit oder ohne Concierge, plus alle unzugewiesenen. Blaue,
// grüne und stornierte Projekte zählen NICHT im Badge.
// 24 Jun 2026 · Farb-KEY zentral in `lib/bookingStatus.js` (`projectColorKey`),
// synchron mit Backend `_red_project_ids`. Hier nur noch bg + Label je Key.
function projectStatusColor(p) {
  const key = projectColorKey(p);
  const raw = p.status || "planning";
  if (raw === "cancelled") return { key, bg: STATUS_COLORS.grey, label: "Storniert" };
  if (key === "green") return { key, bg: STATUS_COLORS.green, label: "Erledigt" };
  if (key === "red") return { key, bg: STATUS_COLORS.red, label: "Überfällig" };
  if (key === "blue") return { key, bg: STATUS_COLORS.blue, label: "In Bearbeitung" };
  const label = !p.assigned_to ? "Nicht zugeteilt" : raw === "on_hold" ? "Pausiert" : "In Planung";
  return { key, bg: STATUS_COLORS.grey, label };
}

// Feb 2026 · 9 Sep 2026 · Sort-Bucket entspricht der neuen Farbregel:
//   0 = GRAU (Offen: nicht zugeteilt / In Planung / Pausiert)
//   1 = BLAU (In Bearbeitung mit Concierge)
//   2 = GRÜN (Erledigt)
//   3 = SLATE (Storniert)
// Innerhalb eines Buckets: start_date desc (Fallback created_at).
function projectSortBucket(p) {
  const st = p.status || "planning";
  if (st === "cancelled") return 3;
  if (st === "completed") return 2;
  if (st === "in_progress" && p.assigned_to) return 1;
  return 0;
}
function projectSortDate(p) {
  return p.start_date || p.created_at || "";
}

const EMPTY_FORM = { property_id: "", title: "", description: "", start_date: "", end_date: "", budget: "", assigned_to: "" };
const EMPTY_EDIT = { title: "", description: "", start_date: "", end_date: "", budget: "", assigned_to: "", status: "planning", cancellation_reason: "" };

export default function TeamProjects() {
  // Feb 2026 · User-Directive „Zuweisungen sind Admin-only":
  // Projekt-Anlage erzwingt einen Verantwortlichen. Weil nur Admins
  // Zuweisungen setzen dürfen, ist der ganze Anlage-Flow Admin-only.
  const { user, activeConciergeId, activeConciergeName, impersonatedConcierge } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Feb 2026 · 9 Sep 2026 · Bearbeitungs-Modal für bestehende Projekte.
  // `editingProject` hält das Projekt, das gerade bearbeitet wird — null
  // schließt das Modal. `editForm` spiegelt den bearbeitbaren Payload.
  const [editingProject, setEditingProject] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [editBusy, setEditBusy] = useState(false);
  // Feb 2026 · 9 Sep 2026 · Inline-Meilensteine im Bearbeitungs-Modal.
  // Zeitleisten-Anpassungen (Titel, Datum, Status, Löschen, Neu) speichern
  // sofort via bestehender `/projects/{id}/milestones`-Routen — kein
  // Kontextwechsel zur Detail-Seite mehr nötig.
  const [milestones, setMilestones] = useState([]);
  const [newMs, setNewMs] = useState({ title: "", description: "", due_date: "" });
  const [msBusy, setMsBusy] = useState(false);
  // Feb 2026 · 9 Sep 2026 · User-Directive „Jede Änderung an einem Projekt
  // (inkl. Meilensteine) muss mit dem 4-stelligen Admin-PIN bestätigt werden".
  // Session-Unlock-Pattern: erste Mutation im Modal öffnet den PIN-Dialog,
  // nach Erfolg bleibt das Modal für die restliche Session freigeschaltet.
  // `closeEdit` resettet den Unlock — jede erneut geöffnete Bearbeitung
  // erzwingt eine neue PIN-Eingabe. `pendingAction` speichert den Callback,
  // der nach dem PIN-Erfolg ausgeführt wird (nicht in useState-Direkt-Form,
  // sondern via lazy-Wrapper, damit React die Funktion nicht als Reducer
  // interpretiert).
  const [pinUnlocked, setPinUnlocked] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  // Feb 2026 · 9 Sep 2026 · User-Directive „Storno-Reason als Pflichtfeld
  // im Confirm-Dialog". `cancelDialog` hält die aktuell laufende Storno-
  // Abfrage; das Promise wird per `resolveRef` bestätigt/abgebrochen.
  const [cancelDialog, setCancelDialog] = useState(null);
  const cancelResolveRef = useRef(null);

  const load = async () => {
    const [p, pr, tm] = await Promise.all([
      api.get("/projects"),
      api.get("/properties"),
      api.get("/team/members"),
    ]);
    setRows(p.data);
    setProperties(pr.data);
    setTeamMembers((tm.data || []).filter((m) => ["admin", "concierge"].includes(m.role)));
  };
  useEffect(() => { load(); }, []);

  // Wenn das gewählte Objekt einen Standard-Concierge hat, den als Default
  // vorschlagen (Nutzer kann jederzeit anders wählen).
  useEffect(() => {
    if (!form.property_id) return;
    const prop = properties.find((p) => p.id === form.property_id);
    if (prop?.assigned_concierge_id) {
      setForm((f) => (f.assigned_to ? f : { ...f, assigned_to: prop.assigned_concierge_id }));
    }
  }, [form.property_id, properties]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.assigned_to) {
      toast.error("Bitte einen Concierge oder Admin für dieses Projekt zuweisen.");
      return;
    }
    try {
      await api.post("/projects", { ...form, budget: form.budget ? parseFloat(form.budget) : null });
      toast.success("Projekt angelegt.");
      setShowForm(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };

  const openEdit = (project) => {
    setEditingProject(project);
    setEditForm({
      title: project.title || "",
      description: project.description || "",
      start_date: project.start_date || "",
      end_date: project.end_date || "",
      budget: project.budget != null ? String(project.budget) : "",
      assigned_to: project.assigned_to || "",
      status: project.status || "planning",
      cancellation_reason: project.cancellation_reason || "",
    });
    setMilestones(project.milestones || []);
    setNewMs({ title: "", description: "", due_date: "" });
  };

  // Feb 2026 · 9 Sep 2026 · Öffnet den Storno-Reason-Dialog und wartet
  // auf ein Ergebnis. Rückgabe:
  //   • { ok: true, reason: "…" }  → Admin hat bestätigt
  //   • { ok: false }             → Admin hat abgebrochen
  const askCancellationReason = (projectTitle) => new Promise((resolve) => {
    cancelResolveRef.current = resolve;
    setCancelDialog({ projectTitle });
  });

  const closeEdit = () => {
    setEditingProject(null);
    setEditForm(EMPTY_EDIT);
    setEditBusy(false);
    setMilestones([]);
    setNewMs({ title: "", description: "", due_date: "" });
    setPinUnlocked(false);
    setPendingAction(null);
  };

  // Feb 2026 · 9 Sep 2026 · PIN-Success-Handler: nach erfolgreicher PIN-
  // Eingabe wird die zwischengespeicherte `pendingAction` genau einmal
  // ausgeführt. `pinUnlocked=true` unterdrückt weitere PIN-Prompts bis
  // zum Schließen des Modals.
  const onPinSuccess = () => {
    setPinUnlocked(true);
    const run = pendingAction;
    setPendingAction(null);
    if (typeof run === "function") {
      Promise.resolve()
        .then(() => run())
        .catch((err) => {
          toast.error(formatErr(err?.response?.data?.detail) || err?.message || "Aktion fehlgeschlagen.");
        });
    }
  };

  // Feb 2026 · 9 Sep 2026 · Reload einzelnes Projekt inkl. Meilensteine.
  // Nach jeder Meilenstein-Mutation holen wir die kanonische Reihenfolge
  // + `completed_at`-Timestamps direkt vom Server, damit die Modal-Liste
  // dem Server-Zustand entspricht.
  const reloadCurrent = async () => {
    if (!editingProject) return;
    try {
      const { data } = await api.get(`/projects/${editingProject.id}`);
      setMilestones(data.milestones || []);
      // Auch die Karten-Liste im Hintergrund refreshen, damit
      // `milestones_total/_done` und die Progress-Bar stimmig sind.
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  const patchMs = async (mid, patch) => {
    if (!pinUnlocked) {
      setPendingAction(() => () => patchMs(mid, patch));
      return;
    }
    setMsBusy(true);
    try {
      await api.patch(`/projects/${editingProject.id}/milestones/${mid}`, patch);
      await reloadCurrent();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setMsBusy(false);
    }
  };

  const deleteMs = async (mid) => {
    if (!pinUnlocked) {
      setPendingAction(() => () => deleteMs(mid));
      return;
    }
    setMsBusy(true);
    try {
      await api.delete(`/projects/${editingProject.id}/milestones/${mid}`);
      await reloadCurrent();
      toast.success("Meilenstein gelöscht.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setMsBusy(false);
    }
  };

  const addMs = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (!newMs.title?.trim()) {
      toast.error("Meilenstein-Titel darf nicht leer sein.");
      return;
    }
    if (!pinUnlocked) {
      setPendingAction(() => () => addMs());
      return;
    }
    setMsBusy(true);
    try {
      await api.post(`/projects/${editingProject.id}/milestones`, {
        title: newMs.title,
        description: newMs.description || null,
        due_date: newMs.due_date || null,
      });
      setNewMs({ title: "", description: "", due_date: "" });
      await reloadCurrent();
      toast.success("Meilenstein hinzugefügt.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setMsBusy(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editingProject) return;
    if (!editForm.title?.trim()) {
      toast.error("Titel darf nicht leer sein.");
      return;
    }
    // Feb 2026 · 9 Sep 2026 · User-Directive „Zeige beim Wechsel auf
    // Storniert einen kurzen Bestätigungs-Dialog" + „Storno-Reason als
    // Pflichtfeld". Wenn der Status TATSÄCHLICH von einem Nicht-Storno
    // auf Storno wechselt, blocken wir hier auf einer Reason-Eingabe.
    // Vor dem PIN-Prompt: Admin kann versehentliche Auswahl abbrechen,
    // ohne PIN eintippen zu müssen.
    let cancellationReason = null;
    if (editForm.status === "cancelled" && editingProject.status !== "cancelled") {
      const res = await askCancellationReason(editingProject.title);
      if (!res || !res.ok) {
        setEditBusy(false);
        return;
      }
      cancellationReason = res.reason;
    }
    if (!pinUnlocked) {
      setPendingAction(() => () => saveEdit({ preventDefault: () => {} }));
      return;
    }
    setEditBusy(true);
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description || null,
        start_date: editForm.start_date || null,
        end_date: editForm.end_date || null,
        budget: editForm.budget ? parseFloat(editForm.budget) : null,
        status: editForm.status,
      };
      // Zuweisung nur mitschicken, wenn Admin — Concierge dürfte sowieso 403 bekommen.
      if (isAdmin) payload.assigned_to = editForm.assigned_to || null;
      // Storno-Reason: bei einem NEUEN Storno übernehmen wir den Wert aus dem
      // Reason-Dialog; bei einem bereits stornierten Projekt akzeptieren wir
      // die Bearbeitung direkt aus dem `editForm` (readonly → editierbar).
      if (cancellationReason) {
        payload.cancellation_reason = cancellationReason;
      } else if (
        editingProject.status === "cancelled" &&
        editForm.status === "cancelled" &&
        (editForm.cancellation_reason || "").trim() !== (editingProject.cancellation_reason || "").trim()
      ) {
        payload.cancellation_reason = editForm.cancellation_reason || null;
      }
      await api.patch(`/projects/${editingProject.id}`, payload);
      toast.success("Projekt gespeichert.");
      closeEdit();
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      setEditBusy(false);
    }
  };

  // Feb 2026 · 16 Sep 2026 · User-Directive „Admin-Impersonation zeigt
  // nur zugewiesene Daten": Projekt-Ansicht mirrort die Backend-Logik
  // (`list_projects`) — Concierge sieht Projekte an denen er direkter
  // Verantwortlicher (`assigned_to`) ODER Haupt-Concierge des Objekts
  // (`assigned_concierge_id`) ist. Für echte Concierges filtert das
  // Backend bereits; hier ist es idempotent.
  //
  // Feb 2026 · 16 Sep 2026 · Nachtrag „Fremd-Concierge sieht immer noch
  // alle Projekte": `activeConciergeId` ist nur gesetzt, wenn
  // `effectiveRole === "concierge"`. Bestimmte Einstiegs-Pfade
  // (ConciergePicker in Sidebar, Deep-Link, Kachel-Klick aus /team/team-load)
  // setzen aber nur `impersonatedConcierge` und NICHT die effektive Rolle.
  // Damit der Filter in beiden Fällen greift, nehmen wir das direkte
  // `impersonatedConcierge?.id` als Fallback — sobald der Admin
  // IRGENDWO einen Concierge ausgewählt hat, klammert sich die Sicht.
  const scopeConciergeId = user?.role === "concierge"
    ? user.id
    : (activeConciergeId || impersonatedConcierge?.id || null);

  const visibleRows = useMemo(() => {
    if (!scopeConciergeId) return rows;
    return rows.filter((r) =>
      r.assigned_to === scopeConciergeId
      || r.assigned_concierge_id === scopeConciergeId
    );
  }, [rows, scopeConciergeId]);

  // Feb 2026 · 9 Sep 2026 · Sortierte Ansicht: 4 Buckets (siehe
  // `projectSortBucket`), innerhalb der Buckets nach Datum absteigend.
  const sortedRows = useMemo(() => {
    const arr = [...visibleRows];
    arr.sort((a, b) => {
      const ba = projectSortBucket(a);
      const bb = projectSortBucket(b);
      if (ba !== bb) return ba - bb;
      const da = projectSortDate(a);
      const db = projectSortDate(b);
      if (da === db) return 0;
      // desc: neuere Daten oben
      return db.localeCompare(da);
    });
    return arr;
  }, [visibleRows]);

  // Feb 2026 · 9 Sep 2026 · User-Directive „Der rote Badge zeigt die
  // Gesamtanzahl aller Projekte mit grauer Kachel". Gleiche Bedingung
  // wie `projectStatusColor().key === 'grey'` — also alle Projekte, die
  // Aufmerksamkeit brauchen (nicht zugeteilt, Planung, oder Pausiert).
  const openRedCount = useMemo(() => {
    return visibleRows.filter((p) => projectStatusColor(p).key === "red").length;
  }, [visibleRows]);
  // Feb 2026 · 9 Sep 2026 · User-Directive „Multi-Filter · auch für blaue
  // Kacheln". Analog zum grey-Badge: Zähler + Klick-Toggle, damit der
  // Admin „In Bearbeitung"-Projekte ähnlich isolieren kann.
  const openBlueCount = useMemo(() => {
    return visibleRows.filter((p) => projectStatusColor(p).key === "blue").length;
  }, [visibleRows]);

  // Feb 2026 · 9 Sep 2026 · Gemeinsamer Farbfilter-State — `null` bedeutet
  // „alle anzeigen"; ein Farb-Key filtert entsprechend. Nur EIN Filter
  // gleichzeitig aktiv; Klick auf denselben Badge toggled zurück auf null.
  //
  // Feb 2026 · 10 Feb 2026 — URL-Hydration: Sidebar-Badges (rot/blau) linken
  // jetzt mit `?color=grey|blue` hierher, um die Projektliste direkt vor-
  // gefiltert zu öffnen. Der Query-Param bleibt in der URL sichtbar, damit
  // Team-Mitglieder gefilterte Ansichten teilen können.
  const [searchParams, setSearchParams] = useSearchParams();
  const unseenOnly = searchParams.get("unseen") === "1";
  const initialColor = (() => {
    const raw = searchParams.get("color");
    return raw === "red" || raw === "grey" || raw === "blue" ? raw : null;
  })();
  const [colorFilter, setColorFilter] = useState(initialColor);
  const toggleColorFilter = (key) => {
    setColorFilter((cur) => {
      const next = cur === key ? null : key;
      const params = new URLSearchParams(searchParams);
      if (next) params.set("color", next); else params.delete("color");
      setSearchParams(params, { replace: true });
      return next;
    });
  };
  const filteredRows = useMemo(() => {
    let base = colorFilter ? sortedRows.filter((p) => projectStatusColor(p).key === colorFilter) : sortedRows;
    if (unseenOnly) base = base.filter((p) => isUnseenAssignment(p, scopeConciergeId, "assigned_to"));
    return base;
  }, [sortedRows, colorFilter, unseenOnly, scopeConciergeId]);
  const unseenCount = useMemo(
    () => visibleRows.filter((p) => !["completed", "cancelled"].includes(p.status) && isUnseenAssignment(p, scopeConciergeId, "assigned_to")).length,
    [visibleRows, scopeConciergeId],
  );
  const toggleUnseen = () => {
    const next = new URLSearchParams(searchParams);
    if (unseenOnly) next.delete("unseen"); else next.set("unseen", "1");
    setSearchParams(next, { replace: true });
  };
  const filterLabel = colorFilter === "red"
    ? "Projekte mit Handlungsbedarf"
    : colorFilter === "grey"
      ? "graue Projekte"
      : colorFilter === "blue"
        ? "blaue Projekte · In Bearbeitung"
        : "";
  const filterAccent = colorFilter === "blue" ? STATUS_COLORS.blue : STATUS_COLORS.red;

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-projects">
      <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
        <div>
          <div className="label-caps mb-3">
            Weissblom Team
            {activeConciergeName && (
              <span className="ml-2 text-navy-dark/50 normal-case tracking-normal">
                · Perspektive von {activeConciergeName}
              </span>
            )}
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-navy inline-flex items-center gap-2 flex-wrap">
            Projekte
            {openRedCount > 0 && (
              <button
                type="button"
                onClick={() => toggleColorFilter("red")}
                data-testid="projects-open-badge"
                data-active={colorFilter === "red" ? "true" : "false"}
                title={colorFilter === "red"
                  ? "Filter aktiv — klicken, um alle Projekte wieder anzuzeigen"
                  : `${openRedCount} überfällige${openRedCount === 1 ? "s" : ""} Projekt${openRedCount === 1 ? "" : "e"} — Handlungsbedarf, klicken zum Filtern`}
                className="inline-flex items-center justify-center min-w-[36px] h-9 px-3 rounded-full text-[16px] font-display font-bold leading-none transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  background: STATUS_COLORS.red,
                  color: BRAND_COLORS.ivory,
                  boxShadow: colorFilter === "red" ? `0 0 0 3px ${withAlpha(STATUS_COLORS.red, 0.28)}` : "none",
                }}
                aria-pressed={colorFilter === "red"}
                aria-label={`${openRedCount} überfällige Projekte (Handlungsbedarf) — Filter umschalten`}
              >
                {openRedCount > 99 ? "99+" : openRedCount}
              </button>
            )}
            {openBlueCount > 0 && (
              <button
                type="button"
                onClick={() => toggleColorFilter("blue")}
                data-testid="projects-blue-badge"
                data-active={colorFilter === "blue" ? "true" : "false"}
                title={colorFilter === "blue"
                  ? "Filter aktiv — klicken, um alle Projekte wieder anzuzeigen"
                  : `${openBlueCount} Projekt${openBlueCount === 1 ? "" : "e"} in Bearbeitung — klicken, um nur diese anzuzeigen`}
                className="inline-flex items-center justify-center min-w-[36px] h-9 px-3 rounded-full text-[16px] font-display font-bold leading-none transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  background: STATUS_COLORS.blue,
                  color: BRAND_COLORS.ivory,
                  boxShadow: colorFilter === "blue" ? `0 0 0 3px ${withAlpha(STATUS_COLORS.blue, 0.28)}` : "none",
                }}
                aria-pressed={colorFilter === "blue"}
                aria-label={`${openBlueCount} Projekte in Bearbeitung — Filter umschalten`}
              >
                {openBlueCount > 99 ? "99+" : openBlueCount}
              </button>
            )}
          </h1>
          {(unseenOnly || unseenCount > 0) && (
            <div className="mt-3" data-testid="projects-unseen-filter">
              <SeenFilterToggle active={unseenOnly} count={unseenCount} onToggle={toggleUnseen} testidBase="team-projects-unseen" />
            </div>
          )}
          {colorFilter && (
            <div
              className="mt-3 inline-flex items-center gap-3 text-[12px] tracking-wider uppercase"
              data-testid="projects-filter-chip"
            >
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
                style={{ background: withAlpha(filterAccent, 0.12), color: filterAccent }}
              >
                Filter aktiv · nur {filterLabel} · {filteredRows.length}/{visibleRows.length}
              </span>
              <button
                type="button"
                onClick={() => setColorFilter(null)}
                data-testid="projects-filter-clear"
                className="text-navy-dark/60 hover:text-navy transition-colors"
              >
                × Filter zurücksetzen
              </button>
            </div>
          )}
          <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">
            {activeConciergeId
              ? "Nur Projekte, die diesem Concierge direkt zugewiesen sind."
              : "Größere Vorhaben mit Zeitleiste, Meilensteinen und Foto-Fortschritt."}
          </p>
        </div>
        {isAdmin && (
          <button data-testid="new-project-btn" onClick={() => setShowForm((v) => !v)}
            className="text-ivory px-8 py-3 text-[12px] tracking-[0.15em] uppercase hover:opacity-90"
            style={{ background: BRAND_COLORS.navy }}>{showForm ? "Schließen" : "Neues Projekt"}</button>
        )}
      </div>

      {isAdmin && showForm && (
        <form onSubmit={submit} data-testid="project-form"
          className="bg-white border border-navy/10 p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <select required data-testid="proj-property-select" value={form.property_id}
            onChange={(e) => setForm({ ...form, property_id: e.target.value })}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3">
            <option value="">Objekt wählen</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select
            required
            data-testid="proj-assignee-select"
            value={form.assigned_to}
            onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
          >
            <option value="">Verantwortlichen wählen (Pflicht)</option>
            {teamMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name || m.email} · {m.role === "admin" ? "Admin" : "Concierge"}
              </option>
            ))}
          </select>
          <input required data-testid="proj-title-input" placeholder="Projekttitel"
            value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3" />
          <DatePicker testid="proj-start-date" placeholder="Projektstart"
            value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
          <DatePicker testid="proj-end-date" placeholder="Projektende"
            value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })}
            min={form.start_date || undefined} />
          <input type="number" step="0.01" data-testid="proj-budget-input" placeholder="Budget (EUR)"
            value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}
            className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3" />
          <textarea rows={2} placeholder="Beschreibung" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3" />
          <button data-testid="proj-submit-btn" type="submit"
            className="md:col-span-2 text-ivory py-3 text-[12px] tracking-[0.15em] uppercase hover:opacity-90"
            style={{ background: BRAND_COLORS.navy }}>Projekt anlegen</button>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {filteredRows.length === 0 ? (
          <div className="lg:col-span-2 bg-white border border-navy/10 p-16 text-center">
            <div className="font-display text-2xl text-navy mb-2">
              {colorFilter === "grey"
                ? "Keine grauen Projekte"
                : colorFilter === "blue"
                  ? "Keine Projekte in Bearbeitung"
                  : activeConciergeId ? "Keine zugewiesenen Projekte" : "Keine Projekte"}
            </div>
            <p className="text-[13px] text-navy-dark/60">
              {colorFilter
                ? "Filter zurücksetzen, um weitere zu sehen."
                : activeConciergeId
                  ? `${activeConciergeName || "Der Concierge"} hat aktuell keine direkt zugewiesenen Projekte.`
                  : isAdmin
                    ? "Neues Projekt anlegen, um zu starten."
                    : "Aktuell keine dir zugewiesenen Projekte."}
            </p>
          </div>
        ) : filteredRows.map((p) => {
          const total = p.milestones_total || 0;
          const done = p.milestones_done || 0;
          const pct = total ? Math.round((done / total) * 100) : 0;
          const st = projectStatusColor(p);
          return (
            // Feb 2026 · 9 Sep 2026 · Card ist weiter klickbar (Link), aber
            // der linke Farbstreifen + Status-Pill kommen aus dem system-
            // weiten 4-Farb-Schema (STATUS_COLORS). Bearbeiten-Button liegt
            // absolut oben rechts — stopPropagation verhindert Navigation.
            <div key={p.id} className="relative">
              <Link to={`/team/projects/${p.id}`} data-testid={`project-card-${p.id}`}
                className="block bg-white border border-navy/10 p-6 md:p-7 hover:border-gold/40 transition-colors relative overflow-hidden"
                style={{ borderLeft: `4px solid ${st.bg}` }}>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <SeenDot entity={p} viewerId={scopeConciergeId} testid={`project-seen-${p.id}`} />
                  <StatusBadge
                    colorKey={st.key}
                    label={st.label}
                    striped={p.status === "cancelled"}
                    testid={`project-status-pill-${p.id}`}
                  />
                  <span className="label-caps">{p.property_name} · {STATUS_LABEL[p.status] || p.status}</span>
                </div>
                <h3 className="font-display text-2xl text-navy mb-3 pr-16">{p.title}</h3>
                {p.description && <p className="text-[13px] text-navy-dark/70 mb-4 line-clamp-2">{p.description}</p>}
                {p.status === "cancelled" && p.cancellation_reason && (
                  <div
                    data-testid={`project-cancellation-reason-${p.id}`}
                    className="mb-4 border-l-2 px-3 py-2 text-[12px] leading-relaxed"
                    style={{
                      borderColor: STATUS_COLORS.red,
                      background: withAlpha(STATUS_COLORS.red, 0.06),
                      color: BRAND_COLORS.navy,
                    }}
                  >
                    <span className="label-caps mr-1" style={{ color: STATUS_COLORS.red }}>
                      Storno-Grund
                    </span>
                    {p.cancelled_at && (
                      <span className="text-navy-dark/50">
                        · {deDate(p.cancelled_at)}
                      </span>
                    )}
                    <div className="mt-1 line-clamp-3">{p.cancellation_reason}</div>
                  </div>
                )}
                <div className="flex justify-between items-center text-[12px] text-navy-dark mb-2">
                  <span>{done}/{total} Meilensteine</span>
                  <span className="font-medium text-navy">{pct}%</span>
                </div>
                <div className="h-1 w-full" style={{ background: withAlpha(st.bg, 0.15) }}>
                  <div className="h-1" style={{ width: `${pct}%`, background: st.bg }} />
                </div>
                <div className="mt-4 text-[11px] tracking-widest uppercase text-navy-dark/60 flex justify-between">
                  <span>{p.start_date ? deDate(p.start_date) : "—"} → {p.end_date ? deDate(p.end_date) : "—"}</span>
                  {p.budget && <span>{deCurrency(p.budget, p.currency || "EUR")}</span>}
                </div>
              </Link>
              {isAdmin && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(p); }}
                  data-testid={`project-edit-btn-${p.id}`}
                  title="Projekt bearbeiten"
                  className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 border border-navy/20 bg-white hover:border-gold hover:text-gold transition-colors text-[11px] tracking-[0.12em] uppercase text-navy"
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
                  Bearbeiten
                </button>
              )}
            </div>
          );
        })}
      </div>

      {editingProject && (
        <div
          className="fixed inset-0 z-50 bg-navy/50 flex items-center justify-center p-4"
          onClick={closeEdit}
          data-testid="project-edit-modal-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="project-edit-modal"
            className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10 sticky top-0 bg-white z-10">
              <div>
                <div className="label-caps">Projekt bearbeiten</div>
                <h2 className="font-display text-2xl text-navy mt-1">{editingProject.title}</h2>
              </div>
              <div className="flex items-center gap-3">
                {/* Feb 2026 · 9 Sep 2026 · Visueller Unlock-Indikator.
                    Gold = PIN eingegeben, Session freigeschaltet. Grau =
                    nächste Aktion triggert PIN-Prompt. */}
                <span
                  data-testid="project-edit-pin-indicator"
                  data-unlocked={pinUnlocked ? "true" : "false"}
                  title={pinUnlocked
                    ? "PIN bestätigt · Änderungen werden direkt gespeichert."
                    : "Nächste Änderung fragt den 4-stelligen Admin-PIN ab."}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] tracking-[0.1em] uppercase font-bold"
                  style={{
                    background: pinUnlocked ? withAlpha(BRAND_COLORS.gold, 0.18) : withAlpha(STATUS_COLORS.grey, 0.15),
                    color: pinUnlocked ? BRAND_COLORS.goldDark : BRAND_COLORS.navy,
                  }}
                >
                  <ShieldCheck className="w-3 h-3" strokeWidth={2.2} />
                  {pinUnlocked ? "PIN bestätigt" : "PIN erforderlich"}
                </span>
                <button
                  onClick={closeEdit}
                  data-testid="project-edit-close"
                  className="text-navy-dark/60 hover:text-navy p-1"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            <form onSubmit={saveEdit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="project-edit-form">
              <input
                required
                data-testid="project-edit-title"
                placeholder="Projekttitel"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
              />
              <select
                data-testid="project-edit-status"
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
              >
                <option value="planning">Planung</option>
                <option value="in_progress">In Arbeit</option>
                <option value="on_hold">Pausiert</option>
                <option value="completed">Abgeschlossen</option>
                <option value="cancelled">Storniert</option>
              </select>
              <select
                data-testid="project-edit-assignee"
                value={editForm.assigned_to}
                onChange={(e) => setEditForm({ ...editForm, assigned_to: e.target.value })}
                disabled={!isAdmin}
                className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 disabled:opacity-60"
              >
                <option value="">Verantwortlichen wählen</option>
                {teamMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name || m.email} · {m.role === "admin" ? "Admin" : "Concierge"}
                  </option>
                ))}
              </select>
              <DatePicker
                testid="project-edit-start"
                placeholder="Projektstart"
                value={editForm.start_date}
                onChange={(v) => setEditForm({ ...editForm, start_date: v })}
              />
              <DatePicker
                testid="project-edit-end"
                placeholder="Projektende"
                value={editForm.end_date}
                onChange={(v) => setEditForm({ ...editForm, end_date: v })}
                min={editForm.start_date || undefined}
              />
              <input
                type="number"
                step="0.01"
                data-testid="project-edit-budget"
                placeholder="Budget (EUR)"
                value={editForm.budget}
                onChange={(e) => setEditForm({ ...editForm, budget: e.target.value })}
                className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
              />
              <textarea
                rows={4}
                placeholder="Beschreibung"
                data-testid="project-edit-description"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
              />

              {/* Feb 2026 · 9 Sep 2026 · User-Directive „Storno-Reason
                  nachbearbeiten". Zeigt sich nur, wenn das Projekt aktuell
                  storniert ist — editierbares Textfeld statt reiner
                  Anzeige. Änderung feuert bei Save eine PATCH mit dem
                  neuen `cancellation_reason` (Backend rührt `cancelled_at`
                  nicht mehr an, wenn der Status unverändert bleibt). */}
              {editingProject.status === "cancelled" && (
                <div
                  data-testid="project-edit-cancellation-note"
                  className="md:col-span-2 border-l-4 p-4"
                  style={{
                    borderColor: STATUS_COLORS.red,
                    background: withAlpha(STATUS_COLORS.red, 0.05),
                  }}
                >
                  <label
                    htmlFor="project-edit-cancellation-reason"
                    className="label-caps mb-2 block"
                    style={{ color: STATUS_COLORS.red }}
                  >
                    Storno-Notiz
                    {editingProject.cancelled_at && (
                      <span className="ml-2 text-navy-dark/60 normal-case tracking-normal">
                        · storniert am {deDate(editingProject.cancelled_at)}
                      </span>
                    )}
                  </label>
                  <textarea
                    id="project-edit-cancellation-reason"
                    data-testid="project-edit-cancellation-reason"
                    value={editForm.cancellation_reason || ""}
                    onChange={(e) => setEditForm({ ...editForm, cancellation_reason: e.target.value })}
                    rows={3}
                    placeholder="Storno-Grund verfeinern oder ergänzen …"
                    className="w-full bg-white border border-navy/20 focus:border-red-700 focus:outline-none px-3 py-2 text-[13px] text-navy resize-none"
                  />
                  <div className="text-[11px] text-navy-dark/50 mt-1">
                    Änderungen an dieser Notiz überschreiben den bisherigen Text — der Zeitstempel bleibt erhalten.
                  </div>
                </div>
              )}

              {/* Feb 2026 · 9 Sep 2026 · Inline-Meilensteine.
                  Zeitleisten-Anpassung ohne Kontextwechsel: jede Zeile
                  speichert Titel · Datum · Status sofort; Löschen per
                  Trash-Icon; neue Meilensteine per Sub-Form am Ende. */}
              <div className="md:col-span-2 mt-2" data-testid="project-edit-milestones">
                <div className="flex items-center justify-between mb-3">
                  <div className="label-caps">Meilensteine · Zeitleiste</div>
                  <div className="text-[11px] text-navy-dark/50">
                    {milestones.filter((m) => m.status === "done").length}/{milestones.length} erledigt
                  </div>
                </div>

                <div className="space-y-2">
                  {milestones.length === 0 && (
                    <div className="text-[12px] text-navy-dark/50 italic py-2" data-testid="project-edit-milestones-empty">
                      Noch keine Meilensteine. Füge unten den ersten hinzu.
                    </div>
                  )}
                  {milestones.map((m, idx) => {
                    const stColor = MS_STATUS_COLOR[m.status] || MS_STATUS_COLOR.pending;
                    return (
                      <div
                        key={m.id}
                        data-testid={`project-edit-ms-row-${m.id}`}
                        className="border border-navy/10 bg-white p-3 relative"
                        style={{ borderLeft: `3px solid ${stColor.bg}` }}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_150px_auto] gap-2 items-center">
                          <input
                            type="text"
                            defaultValue={m.title}
                            data-testid={`project-edit-ms-title-${m.id}`}
                            placeholder={`Schritt ${idx + 1}`}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v && v !== m.title) patchMs(m.id, { title: v });
                              else if (!v) e.target.value = m.title;
                            }}
                            className="bg-white border border-navy/15 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
                          />
                          <DatePicker
                            testid={`project-edit-ms-due-${m.id}`}
                            placeholder="Fällig am"
                            value={m.due_date || ""}
                            onChange={(v) => {
                              if ((v || null) !== (m.due_date || null)) patchMs(m.id, { due_date: v || null });
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => deleteMs(m.id)}
                            disabled={msBusy}
                            data-testid={`project-edit-ms-delete-${m.id}`}
                            title="Meilenstein löschen"
                            className="p-2 border border-navy/15 text-navy-dark/60 hover:text-red-700 hover:border-red-700 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="mt-2 flex gap-1.5 flex-wrap">
                          {["pending", "in_progress", "done"].map((s) => {
                            const c = MS_STATUS_COLOR[s];
                            const active = m.status === s;
                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() => { if (!active) patchMs(m.id, { status: s }); }}
                                disabled={msBusy}
                                data-testid={`project-edit-ms-status-${m.id}-${s}`}
                                className="text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 border transition-colors disabled:opacity-60"
                                style={
                                  active
                                    ? { background: c.bg, color: c.fg, borderColor: c.bg }
                                    : { borderColor: withAlpha(c.bg, 0.35), color: c.bg, background: withAlpha(c.bg, 0.06) }
                                }
                              >
                                {MS_STATUS_LABEL[s]}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Sub-Form: neuen Meilenstein hinzufügen */}
                <div
                  className="mt-3 border border-dashed border-navy/25 bg-white p-3"
                  data-testid="project-edit-ms-add-form"
                >
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_150px_auto] gap-2">
                    <input
                      type="text"
                      value={newMs.title}
                      onChange={(e) => setNewMs({ ...newMs, title: e.target.value })}
                      placeholder="Neuer Meilenstein"
                      data-testid="project-edit-ms-new-title"
                      className="bg-white border border-navy/15 focus:border-gold focus:outline-none px-3 py-2 text-[13px]"
                    />
                    <DatePicker
                      testid="project-edit-ms-new-due"
                      placeholder="Fällig am"
                      value={newMs.due_date}
                      onChange={(v) => setNewMs({ ...newMs, due_date: v })}
                    />
                    <button
                      type="button"
                      onClick={addMs}
                      disabled={msBusy || !newMs.title?.trim()}
                      data-testid="project-edit-ms-new-add"
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] tracking-[0.12em] uppercase text-ivory disabled:opacity-50"
                      style={{ background: BRAND_COLORS.navy }}
                    >
                      <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                      Hinzufügen
                    </button>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  data-testid="project-edit-cancel"
                  className="text-[12px] tracking-wider uppercase text-navy-dark/70 hover:text-navy px-4 py-2.5"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={editBusy}
                  data-testid="project-edit-save"
                  className="text-ivory px-8 py-3 text-[12px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-60"
                  style={{ background: BRAND_COLORS.navy }}
                >
                  {editBusy ? "Wird gespeichert…" : "Speichern"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Feb 2026 · 9 Sep 2026 · PIN-Prompt vor jeder Projekt-Änderung.
          Wird gerendert sobald eine `pendingAction` gesetzt ist — bleibt
          nach Erfolg über `pinUnlocked=true` unterdrückt für den Rest
          der Modal-Session. */}
      {editingProject && pendingAction && !pinUnlocked && (
        <AdminPinPromptDialog
          title="Projekt-Änderung bestätigen"
          description={`Bitte den 4-stelligen Admin-PIN eingeben, um Änderungen an „${editingProject.title}" zu speichern.`}
          purpose="project-edit"
          onClose={() => setPendingAction(null)}
          onSuccess={onPinSuccess}
        />
      )}

      {/* Feb 2026 · 9 Sep 2026 · Storno-Reason-Dialog. Öffnet sich beim
          Status-Wechsel auf `cancelled`. Reason ist Pflichtfeld — der
          Speichern-Button bleibt disabled bis ein nicht-leerer Text
          eingegeben ist. Wird auf Bestätigung mit `{ok:true, reason}`
          resolved, sonst `{ok:false}`. Der Storno-Grund landet im
          PATCH-Payload als `cancellation_reason` und bleibt als
          interne Notiz permanent am Projekt. */}
      {cancelDialog && (
        <CancelReasonDialog
          projectTitle={cancelDialog.projectTitle}
          onCancel={() => {
            const r = cancelResolveRef.current;
            cancelResolveRef.current = null;
            setCancelDialog(null);
            if (typeof r === "function") r({ ok: false });
          }}
          onConfirm={(reason) => {
            const r = cancelResolveRef.current;
            cancelResolveRef.current = null;
            setCancelDialog(null);
            if (typeof r === "function") r({ ok: true, reason });
          }}
        />
      )}
    </div>
  );
}

/**
 * Feb 2026 · 9 Sep 2026 · CancelReasonDialog — Pflicht-Reason vor jeder
 * Projekt-Stornierung. Baut auf der optischen Sprache der übrigen
 * Modals auf (fixed inset + weißer Modal-Body mit navy-Border).
 */
function CancelReasonDialog({ projectTitle, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();
  const canConfirm = trimmed.length >= 3;
  return (
    <div
      className="fixed inset-0 z-[60] bg-navy/60 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="project-cancel-reason-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="project-cancel-reason-dialog"
        role="alertdialog"
        aria-labelledby="cancel-reason-title"
        className="bg-white w-full max-w-xl"
        style={{ borderLeft: `4px solid ${STATUS_COLORS.red}` }}
      >
        <div className="px-6 py-5 border-b border-navy/10">
          <div className="label-caps mb-1" style={{ color: STATUS_COLORS.red }}>
            Bestätigung erforderlich
          </div>
          <h2 id="cancel-reason-title" className="font-display text-2xl text-navy">
            Projekt „{projectTitle}" stornieren?
          </h2>
          <p className="text-[13px] text-navy-dark/70 mt-2 leading-relaxed">
            Alle Meilensteine bleiben archiviert. Der Status wird auf „Storniert" gesetzt und
            das Projekt rutscht in der Übersicht ans Ende der Liste. Diese Aktion ist reversibel
            — du kannst den Status später wieder auf Planung/In Arbeit zurücksetzen. Der
            Storno-Grund bleibt als interne Notiz am Projekt für spätere Rückschau.
          </p>
        </div>
        <div className="px-6 py-5">
          <label
            htmlFor="cancel-reason-textarea"
            className="block text-[11px] tracking-[0.15em] uppercase text-navy-dark/70 mb-2"
          >
            Warum wird das Projekt storniert? <span className="text-red-700">*</span>
          </label>
          <textarea
            id="cancel-reason-textarea"
            data-testid="project-cancel-reason-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder={`Kurze Begründung — z. B. „Auftraggeber hat abgesagt", „Neubau statt Renovierung", …`}
            className="w-full bg-white border border-navy/20 focus:border-red-700 focus:outline-none px-3 py-2 text-[13px] text-navy resize-none"
            autoFocus
          />
          <div className="text-[11px] text-navy-dark/50 mt-1">
            Mindestens 3 Zeichen — dieser Text wird permanent am Projekt gespeichert.
          </div>
        </div>
        <div className="px-6 py-4 border-t border-navy/10 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            data-testid="project-cancel-reason-abort"
            className="text-[12px] tracking-wider uppercase text-navy-dark/70 hover:text-navy px-4 py-2.5"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => canConfirm && onConfirm(trimmed)}
            disabled={!canConfirm}
            data-testid="project-cancel-reason-confirm"
            className="text-ivory px-6 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            style={{ background: STATUS_COLORS.red }}
          >
            <Ban className="w-3.5 h-3.5" strokeWidth={2} />
            Ja, stornieren
          </button>
        </div>
      </div>
    </div>
  );
}
